import { AsyncThunk, AsyncThunkOptions, createAsyncThunk, Dispatch } from '@reduxjs/toolkit';
import { performanceService } from 'common/utilities/PerformanceService';

import { ApiCallResultStatus } from './apiCallResultStatus';
import { RootState } from './hooks';

type ThunkApiConfig = { state: RootState; dispatch?: Dispatch };
type ThunkApi = { dispatch: Dispatch; getState: () => RootState };

/**
 * {@link createAsyncThunk} wrapper for creating a API request thunk that is time-measured using {@link performanceService}.
 *
 * @param typePrefix
 * @param payloadCreator
 * @param options
 */
export const createAsyncApiThunk = <Returned, ThunkArg = void>(
  typePrefix: string,
  payloadCreator: (arg: ThunkArg, thunkApi: ThunkApi) => Promise<Returned>,
  options?: AsyncThunkOptions<ThunkArg, ThunkApiConfig>
): AsyncThunk<Returned, ThunkArg, ThunkApiConfig> =>
  createAsyncThunk(
    typePrefix,
    (arg, thunkAPI) => performanceService.measure(typePrefix, () => payloadCreator(arg, thunkAPI)),
    options
  );

/**
 * Helper {@link AsyncThunk} creator for creating an API request thunk that is only executed if the API has not been previously
 * called (according the the API status yielded by {@link getApiStatus}).
 *
 * Suitable for idempotent APIs that are expected to return the same result unless there are user action APIs in between.
 *
 * Thunk can also be forced to be executed by dispatching `someApiThunk.forceRequest(...)` instead of `someApiThunk(...)`.
 *
 * @param typePrefix
 * @param payloadCreator
 * @param getApiStatus - getter for getting the tracking API status from the state
 * @param options
 */
export function createOneOffAsyncApiThunk<Returned, ThunkArg = void>(
  typePrefix: string,
  payloadCreator: (arg: ThunkArg, thunkApi: ThunkApi) => Promise<Returned>,
  getApiStatus: (state: RootState, arg: ThunkArg) => ApiCallResultStatus,
  options?: AsyncThunkOptions<ThunkArg, ThunkApiConfig>
): AsyncThunk<Returned, ThunkArg, ThunkApiConfig> & { forceRequest: AsyncThunk<Returned, ThunkArg, ThunkApiConfig> } {
  // unconditional thunk
  const thunk = createAsyncApiThunk(typePrefix, payloadCreator, options);

  const conditionalThunk = createAsyncApiThunk(typePrefix, payloadCreator, {
    ...options,
    condition: (arg, { getState }) =>
      // Proceed only if the API has not been previously called (according to the API status returned by `getApiStatus`)
      getApiStatus(getState(), arg) === ApiCallResultStatus.DEFAULT,
  });

  return Object.assign(conditionalThunk, {
    // augment the conditional thunk with the unconditional thunk for forcing the request
    forceRequest: thunk,
  });
}
